import { PublicLayout } from "@/components/PublicLayout";
import { FileText } from "lucide-react";

const LAST_UPDATED = "October 21, 2026";

const sections = [
  {
    id: "acceptance",
    title: "1. Acceptance of Terms",
    body: [
      "These Terms of Service (\"Terms\") govern your access to and use of the Sparkd dating application and related services (the \"Service\"), operated by Sparkd (\"we\", \"us\", \"our\"). By creating an account or using the Service, you agree to be bound by these Terms and our Privacy Policy.",
      "If you do not agree to these Terms, you must not access or use the Service.",
    ],
  },
  {
    id: "eligibility",
    title: "2. Eligibility & Identity Verification",
    body: [
      "Age Gate: users under 18 years of age are strictly forbidden. You must be at least 18 to create an account or use Sparkd. By using the Service you represent and warrant that you are 18 or older. Accounts found to belong to anyone under 18 will be terminated immediately.",
      "You agree to provide accurate information and, where requested, to complete identity or photo verification. We may use verification signals to help keep the community authentic and safe. Providing false information, impersonating another person, or creating an account on behalf of someone else is strictly prohibited.",
    ],
  },
  {
    id: "community",
    title: "3. Community Guidelines & Conduct",
    body: [
      "Sparkd maintains a zero-tolerance policy toward abuse. You agree not to engage in, post, or transmit any of the following:",
      "• Harassment, bullying, stalking, or threats toward any member.",
      "• Hate speech or content that promotes discrimination based on race, ethnicity, religion, gender, sexual orientation, disability, or any protected characteristic.",
      "• Non-consensual content, including intimate images shared without consent, or any sexually explicit content involving minors.",
      "• Solicitation, prostitution, commercial sex, spam, scams, or any request for money from other members.",
      "• Non-consensual recordings of any person, explicit nudity, and stalking in any form, online or in person.",
      "Sparkd enforces a strict zero-tolerance policy for objectionable content or abusive users. Sparkd will remove violating content and immediately terminate and device-ban offending accounts within 24 hours of notification.",
      "UGC & Zero Tolerance: violations of these rules are enforceable by immediate account termination and device-level ID bans, and may be reported to law enforcement where required.",
    ],
  },
  {
    id: "video",
    title: "4. Video Call Code of Conduct",
    body: [
      "Sparkd offers in-app video calling powered by WebRTC. When using video features, you agree to the following code of conduct:",
      "• No recording, screen-capturing, or redistributing any video call or its participants without the explicit consent of everyone involved.",
      "• No explicit, sexual, violent, or otherwise inappropriate material, including nudity, during calls.",
      "• No unsolicited exposure or content. Every participant may end a call at any time.",
      "Reported violations of the video code of conduct are treated with the same zero-tolerance standard as our community guidelines and may lead to permanent removal from the Service.",
    ],
  },
  {
    id: "subscriptions",
    title: "5. Subscriptions & Billing",
    body: [
      "Sparkd offers optional paid tiers: Gold at $14.99/month or $99/year, Platinum at $29.99/month, Diamond at $59.99/month, and the Founder Annual Pass at $99/year. Prices and included features are displayed at the time of purchase.",
      "Subscriptions automatically renew at the end of each billing period (monthly or annually, as selected) unless auto-renewal is turned off at least 24 hours before the end of the current period. Your account is charged for renewal within 24 hours prior to the end of the current period. In-app subscriptions are managed and cancelled through your iOS (Apple ID) or Google Play account settings.",
      "Consumable Sales: Skip the Chat video calls ($2.99 for 1 / $6.99 for 3), Ignites, Roses, and Boosts are non-refundable virtual items that are consumed upon activation.",
      "You can manage or cancel your subscription at any time through your App Store or Google Play account settings. Cancellation takes effect at the end of the current paid period; you will retain access to paid features until then. Except where required by law, payments are non-refundable and we do not provide refunds or credits for partial periods.",
    ],
  },
  {
    id: "venues",
    title: "Physical Premises & Venue Liability Waiver",
    body: [
      "Sparkd does not own, operate, inspect, or manage any partner venue, park, or establishment, or any event listed in the Service.",
      "You assume all personal risk when traveling to, entering, or attending any physical venue or event, and you release Sparkd from liability for incidents occurring on such premises to the fullest extent permitted by law.",
    ],
  },
  {
    id: "wingman-consent",
    title: "Wingman & Emergency Contact Consent",
    body: [
      "By adding a wingman or emergency contact to your squad, you represent and warrant that this person has given consent to receive automated emergency and location SMS messages from Sparkd on your behalf.",
    ],
  },
  {
    id: "content",
    title: "6. Your Content & License",
    body: [
      "You retain ownership of the photos, text, and other content you submit. By posting content, you grant Sparkd a worldwide, non-exclusive, royalty-free license to host, store, display, and distribute that content solely for the purpose of operating and providing the Service.",
      "You are solely responsible for the content you share and represent that you have all rights necessary to share it.",
    ],
  },
  {
    id: "termination",
    title: "7. Account Termination",
    body: [
      "You may delete your account at any time using our Account Deletion Request page. We may suspend or terminate your account, with or without notice, if you violate these Terms, create risk or legal exposure for us, or engage in conduct that harms other members.",
      "Upon termination, your right to use the Service ceases immediately. Sections of these Terms that by their nature should survive termination (including content licenses granted before termination, disclaimers, and dispute resolution) will survive.",
    ],
  },
  {
    id: "disclaimers",
    title: "8. Disclaimers & Limitation of Liability",
    body: [
      "The Service is provided \"as is\" and \"as available\" without warranties of any kind. Sparkd does not conduct criminal background checks and is not responsible for the conduct of any member. You are responsible for your own interactions and should exercise caution and good judgment, especially when meeting people in person.",
      "To the fullest extent permitted by law, Sparkd will not be liable for any indirect, incidental, or consequential damages arising from your use of the Service.",
    ],
  },
  {
    id: "arbitration",
    title: "9. Dispute Resolution & Arbitration",
    body: [
      "Please read this section carefully. Except where prohibited by law, you and Sparkd agree that any dispute, claim, or controversy arising out of or relating to these Terms or the Service will be resolved by binding individual arbitration, rather than in court, except that you may assert claims in small-claims court if they qualify.",
      "You and Sparkd waive any right to a jury trial and agree that disputes will be resolved on an individual basis only — class actions and class-wide arbitration are not permitted. You may opt out of this arbitration agreement within 30 days of first accepting these Terms by contacting support@sparkd-app.com.",
    ],
  },
  {
    id: "changes",
    title: "10. Changes & Contact",
    body: [
      "We may update these Terms from time to time. We will notify you of material changes through the app or by email and update the \"Last updated\" date above. Continued use of the Service after changes take effect constitutes acceptance.",
      "Questions about these Terms? Contact us at support@sparkd-app.com.",
    ],
  },
];

export default function Terms() {
  return (
    <PublicLayout>
      <div className="spark-grid-bg border-b border-[#2A313C]">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-14">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#2A313C] bg-[#1F242D] text-xs uppercase font-semibold tracking-[0.2em] text-[#E11D48]">
            <FileText className="w-3.5 h-3.5" /> Legal
          </span>
          <h1 className="mt-5 font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-white" data-testid="terms-title">
            Terms of Service
          </h1>
          <p className="mt-3 text-slate-400">Last updated: {LAST_UPDATED}</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-12 grid lg:grid-cols-[220px_1fr] gap-10">
        <nav className="hidden lg:block sticky top-24 self-start">
          <p className="text-xs uppercase font-semibold tracking-[0.2em] text-slate-500 mb-3">On this page</p>
          <ul className="space-y-2 text-sm">
            {sections.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`} className="text-slate-400 hover:text-[#F59E0B] transition-colors">
                  {s.title}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <article className="space-y-8">
          {sections.map((s) => (
            <section key={s.id} id={s.id} className="scroll-mt-24 rounded-2xl spark-card p-6 sm:p-8">
              <h2 className="font-heading text-xl sm:text-2xl font-bold text-white mb-4">{s.title}</h2>
              <div className="space-y-3">
                {s.body.map((p, i) => (
                  <p key={i} className="text-[#CBD5E1] leading-relaxed text-[15px]">{p}</p>
                ))}
              </div>
            </section>
          ))}
        </article>
      </div>
    </PublicLayout>
  );
}
